import React, { Fragment, ReactNode, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

import moment, { Moment } from "moment";

import "./OpportunityDrawer.less";

import Row from "antd/lib/row";
import Col from "antd/lib/grid/col";
import Select from "antd/lib/select";
import { FormComponentProps } from "@ant-design/compatible/lib/form";
import { Form } from "@ant-design/compatible";
import Input from "antd/lib/input";
import { DrawerProps } from "antd/lib/drawer";
import InputNumber from "antd/lib/input-number";

import AddClientDrawer from "@pages/Drawers/Clients/Client/Add";
import AddClientContactDrawer from "@pages/Drawers/Clients/ClientContact/Add";
import { shouldShowAddEntityActions } from "@/Utils";

import { processCustomFieldFormValues } from "@/Utils";
import { isAllPipelinesFilter } from "@/Utils/Opportunity";
import { getCurrencySettings } from "@/Utils/Currency";

import { TableQueryFilter, ZomentumRoutes, FormKeys } from "@/Contracts";
import {
	capitalizeFirstLetter,
	ChildEntityHydrationParams,
	filterOptionsOnSearch,
	getCustomFieldsDefaultTypeValue
} from "@/Contracts/Utils";
import {
	AddOpportunityFormValues,
	OpportunityType,
	AddOpportunityRequest,
	Opportunity,
	OpportunityCostAndRevenueData
} from "@/Contracts/Opportunity";
import {
	ClientUserContactType,
	Client,
	ClientContact,
	ClientsRequest,
	ClientContactRequest
} from "@/Contracts/Clients";
import { OpportunityStage, Pipeline } from "@/Contracts/Pipelines";
import {
	CustomField,
	CustomFieldMandatoryFieldDetailsValues
} from "@/Contracts/Settings";
import {
	AllRolePermissions,
	PricingPlanNames,
	UserRole
} from "@/Contracts/Users";

import OpportunitiesActionCreators from "@/Actions/Opportunity";
import { useMultiCurrencyEnabled } from "@/Hooks/useMultiCurrencyEnabled";
import { useCurrencyConfig } from "@/Hooks/useCurrencyConfig";
import OpportunityCurrencyDropdown from "@/V2Components/UI/molecules/OpportunityCurrencyDropdown";
import ClientsActionCreators from "@/Actions/Clients";
import { PipelinesActionCreators } from "@/Actions/Pipelines";
import { getClientContacts } from "@/Services/Clients";
import filter from "lodash/filter";
import RenderCustomFieldFormItem from "@molecules/ZRenderCustomFields";
import { ZLostTag, ZWonTag } from "@atoms/ZTerminalStageTag";
import ZSelect from "@atoms/ZSelect";
import ZMessage from "@molecules/ZMessage";
import ZDynamicSelect from "@molecules/ZDynamicSelect";
import ZCheckbox from "@atoms/ZCheckbox";
import ZInputNumber, { InputNumberWidths } from "@atoms/ZInputNumber";
import ZTypography from "@atoms/ZTypography";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import ZDatePicker from "@molecules/ZDatePicker";
import ZOppRevenueAndCostFields from "@organisms/ZOppRevenueAndCostFields";
import ZFeatureAccess from "@atoms/ZFeatureAccess";
import usePermissions from "@/Hooks/usePermissions";
import { Modules, ModulePermission } from "@/Contracts/Permission";
import { useAccessLevel } from "@/Hooks/useAccessLevel";
import ZClientSelect from "@organisms/ZClientSelect";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import { GlobalState } from "@/Reducers/interface";
import { useGuideHook } from "@/Onboarding";
import { OnboardingState } from "@/Onboarding/store/reducers";
import {
	Steps,
	Stages,
	Modules as OnboardingModules,
	UpdateCheckListBarDataPayload,
	UpdateCheckListBarDataRequest
} from "@/Onboarding/contracts";
import {
	useOnboardingSelector,
	useOnboardingDispatch
} from "@/Onboarding/store";
import OnboardingActionCreators from "@/Onboarding/store/actions";
import { Guide } from "@/Onboarding/hooks/interfaces";
import ZModalConfirm from "@atoms/ZModalConfirmation";
import ShareOpportunityWithVendorDrawer from "@pages/Drawers/Opportunities/ShareOpportunityWithVendorDrawer";
import ZButton from "@atoms/ZButton";
import SettingsActionCreators from "@/Actions/Settings";
import ZDrawer from "@molecules/ZDrawer";
import { ShareOpportunityWithVendorRequest } from "@/Contracts/Opportunity";
import { IOptionProps } from "@atoms/ZSelect/interface";
import ZUserSelect from "@organisms/ZUserSelect";
import useDispatch from "@/Components/Utils/useDispatch";
import { useLocation } from "react-router-dom";
import { useFeatureFlag } from "@/Hooks/useFeature";

interface AddOpportunityGuideDetail {
	readonly SaveOpportunityButtonGuide?: React.ElementType | null;
	readonly handleNext?: () => void;
	readonly stepDetail?: Guide | null;
	readonly OpportunityDrawerGuide?: React.ElementType | null;
	readonly OpportunityDrawerStepDetail?: Guide | null;
	readonly ShareWithVendorCheckboxGuide?: React.ElementType | null;
	readonly ShareWithVendorCheckboxStepDetail?: Guide | null;
	readonly demoClientsData?: Client;
	readonly isOpportunityDrawerActive?: boolean;
	readonly updateCreatedOpportunityDetail?: (
		entityId: string
	) => Promise<void>;
	readonly shareWithVendorCheckBoxChecked?: boolean;
	readonly onboardingActiveStep: Steps | null;
}

export interface Props
	extends DrawerProps,
		FormComponentProps<AddOpportunityFormValues> {
	readonly activeClientCompanyId?: string | null;
	readonly activeClientOwnerId?: string | null;
	readonly activeClientName?: string | null;
	readonly activeClientUserId?: string | null;
	readonly activeOpportunityType: OpportunityType | null;
	readonly activeOpportunityStage?: string | null;
	readonly shouldRedirectToOpportunity?: boolean;
	readonly onSubmit?: (opportunity: Opportunity) => void;
	readonly opportunityRevenueAndCostFields?: boolean;
	readonly addClientContactOnSubmit?: (clientContact: ClientContact) => void;
	readonly isMarketplacePipelineSyncingEnabled: boolean;
}

const formAlignment = {
	labelCol: { span: 6 },
	wrapperCol: { span: 18 }
};

export const AddOpportunityDrawerForm: React.FC<Props> = props => {
	const isMultiCurrencyEnabled = useMultiCurrencyEnabled();
	const { config: currencyConfig } = useCurrencyConfig();
	const showCurrencyDropdown =
		isMultiCurrencyEnabled &&
		(currencyConfig?.currencyConfigs?.length ?? 0) > 0;
	const showPartnerAlignFeaturesFlag = useFeatureFlag(
		"show_partner_align_features"
	);
	const initialEstimatedCloseDate = moment().add(30, "days");
	const isClientAssigned = useAccessLevel(props.activeClientOwnerId);
	const onboardingdispatch = useOnboardingDispatch();
	const location = useLocation();
	const opportunityAssignPermission = usePermissions(
		Modules.OPPORTUNITY,
		ModulePermission.EDIT_ALL,
		[AllRolePermissions.OPPORTUNITY_ASSIGN]
	);
	const hasOpportunityAssignPermission = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.SecondaryRole,
		opportunityAssignPermission
	);
	const clientCompanyPermssions = usePermissions(
		Modules.CLIENT_COMPANY,
		ModulePermission.CREATE_EDIT_DELETE_OWNED
	);
	const {
		GuideUI: OpportunityDrawerGuide,
		stepDetail: OpportunityDrawerStepDetail
	} = useGuideHook({
		stageId: Stages.ADD_OPPORTUNITY,
		stepId: Steps.HIGHLIGHT_ADD_OPPORTUNITY_DRAWER
	});

	const {
		GuideUI: ShareWithVendorCheckboxGuide,
		stepDetail: ShareWithVendorCheckboxStepDetail
	} = useGuideHook({
		stageId: Stages.ADD_OPPORTUNITY,
		stepId: Steps.HIGHLIGHT_OPPORTUNITY_SHARE_WITH_VENDOR_CHECKBOX
	});

	const {
		GuideUI: SaveOpportunityButtonGuide,
		handleNext,
		stepDetail
	} = useGuideHook({
		stageId: Stages.ADD_OPPORTUNITY,
		stepId: Steps.HIGHLIGHT_OPPORTUNITY_SAVE_BUTTON
	});

	const { demoClientsData, checkListBarData, moduleOrder } =
		useOnboardingSelector((state: OnboardingState) => {
			return {
				demoClientsData: state.clientDetail as Client,
				activeStep: state.currentGuideProgress.activeStep,
				activeStage: state.currentGuideProgress.activeStage,
				checkListBarData: state.checkListBarData,
				moduleOrder: state.moduleOrder
			};
		});

	const isOpportunityDrawerActive = useOnboardingSelector(
		(state: OnboardingState) => {
			const { activeModule, activeStage, activeStep } =
				state.currentGuideProgress;
			return (
				activeModule === OnboardingModules.OPPORTUNITY &&
				(activeStep === Steps.HIGHLIGHT_ADD_OPPORTUNITY_DRAWER ||
					Steps.HIGHLIGHT_OPPORTUNITY_SHARE_WITH_VENDOR_CHECKBOX ||
					Steps.HIGHLIGHT_ADD_OPPORTUNITY_BUTTON) &&
				activeStage === Stages.ADD_OPPORTUNITY
			);
		}
	);

	const shareWithVendorCheckBoxChecked = useOnboardingSelector(
		(state: OnboardingState) => {
			const { activeModule, activeStage, activeStep } =
				state.currentGuideProgress;
			return (
				activeModule === OnboardingModules.OPPORTUNITY &&
				activeStep === Steps.HIGHLIGHT_OPPORTUNITY_SAVE_BUTTON &&
				activeStage === Stages.ADD_OPPORTUNITY
			);
		}
	);

	const updateCreatedOpportunityDetail = async (entityId: string) => {
		if (!isOpportunityDrawerActive) {
			return;
		}
		const allModules = checkListBarData?.modules;
		const activeModuleIndex = moduleOrder.findIndex(
			eachModule => eachModule === OnboardingModules.OPPORTUNITY
		);

		if (!!allModules?.[activeModuleIndex]) {
			allModules[activeModuleIndex].created_entity_id = entityId;
			allModules[activeModuleIndex].created_entity_type = "opportunity";
		}

		const requestPayload: UpdateCheckListBarDataPayload = {
			modules: allModules
		};
		const request: UpdateCheckListBarDataRequest = {
			requestPayload,
			hasAPIUpdate: false
		};

		onboardingdispatch(
			OnboardingActionCreators.updateCheckListBarData(request)
		);
	};

	const history = useHistory();

	const addOpportunityGuideDetail: AddOpportunityGuideDetail = {
		OpportunityDrawerGuide,
		OpportunityDrawerStepDetail,
		ShareWithVendorCheckboxGuide,
		ShareWithVendorCheckboxStepDetail,
		SaveOpportunityButtonGuide,
		handleNext,
		stepDetail,
		demoClientsData,
		isOpportunityDrawerActive,
		updateCreatedOpportunityDetail,
		shareWithVendorCheckBoxChecked,
		onboardingActiveStep: null
	};

	const {
		isLoading,
		isClientsLoading,
		isUserLoading,
		clientListData,
		clientContactList,
		userData,
		pipelineList,
		currentActivePipeline,
		nextOpportunityId,
		isFormEdited,
		customFieldListOpportunity,
		defaultMonthsForMargin
	} = useSelector((state: GlobalState) => {
		return {
			isLoading: state.opportunities.isLoading,
			isClientsLoading: state.clients.isLoading,
			isUserLoading: state.user.isLoading,
			clientListData: state.clients.clientList,
			clientContactList: state.clients.clientContactList,
			userData: state.user.userData,
			pipelineList: state.pipelines.pipelineList,
			currentActivePipeline: state.pipelines.currentActivePipeline,
			nextOpportunityId:
				state.user.userCompany?.settings.opportunity_quantifier?.number,
			isFormEdited: state.settings.isFormEdited,
			customFieldListOpportunity:
				state.customFields.customFieldListWithoutFormulaFields
					.opportunity,
			defaultMonthsForMargin:
				state.user.userCompany?.settings
					.default_month_for_opportunity_margin ?? 12
		};
	});
	const dispatch = useDispatch();

	const showOpportunitiesLoaderDispatch = () =>
		dispatch(OpportunitiesActionCreators.showOpportunitiesLoader());
	const hideOpportunitiesLoaderDispatch = () =>
		dispatch(OpportunitiesActionCreators.hideOpportunitiesLoader());
	const addOpportunityDispatch = (
		addOpportunityRequest: AddOpportunityRequest,
		shareOpportunityWithVendorRequest?: ShareOpportunityWithVendorRequest,
		useChangeSuggestionFlowFlag?: boolean
	) =>
		dispatch(
			OpportunitiesActionCreators.addOpportunity(
				addOpportunityRequest,
				shareOpportunityWithVendorRequest,
				useChangeSuggestionFlowFlag
			)
		);
	const getClientsDispatch = (clientsRequest: ClientsRequest) =>
		dispatch(ClientsActionCreators.getClients(clientsRequest));
	const resetClientsDispatch = () =>
		dispatch(ClientsActionCreators.resetClients());
	const getClientContactsDispatch = (payload: ClientContactRequest) =>
		dispatch(ClientsActionCreators.getClientContacts(payload));
	const resetClientContactsDispatch = () =>
		dispatch(ClientsActionCreators.resetClientContacts());
	const getAllPipelinesDispatch = () =>
		dispatch(PipelinesActionCreators.getAllPipelines());
	const resetFormEditedDispatch = () =>
		dispatch(
			SettingsActionCreators.resetFormEdited(FormKeys.AddOpportunity)
		);
	const setFormEditedDispatch = () =>
		dispatch(SettingsActionCreators.setFormEdited(FormKeys.AddOpportunity));

	const [selectedPrimaryClientContact, setSelectedPrimaryClientContact] =
		useState<string | null>(null);

	const [defaultClientUserContactType, setDefaultClientUserContactType] =
		useState<ClientUserContactType>(ClientUserContactType.SecondaryContact);
	const [opportunityStages, setOpportunityStages] = useState<
		OpportunityStage[]
	>([]);
	const [lossStageSelected, setLossStageSelected] = useState<
		boolean | undefined
	>(undefined);
	const [winStageSelected, setWinStageSelected] = useState<
		boolean | undefined
	>(undefined);
	const [customFieldList, setCustomFieldList] = useState<CustomField[]>(
		customFieldListOpportunity
	);
	const [selectedClient, setSelectedClient] = useState<string | null>(
		props.activeClientCompanyId ?? null
	);
	const [clientList, setClientList] = useState<Client[]>(new Array<Client>());
	const [estimatedValue, setEstimatedValue] = useState<number | undefined>(
		undefined
	);
	const [estimatedCloseDate, setEstimatedCloseDate] = useState<
		Moment | null | undefined
	>(initialEstimatedCloseDate);
	const [isOpportunityLoading, setIsOpportunityLoading] =
		useState<boolean>(false);

	const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(
		null
	);
	const [isPipelinesLoading, setIsPipelinesLoading] = useState<boolean>(true);
	const [isClientLoading] = useState<boolean>(false);
	const [contactList, setContactList] = useState<ClientContact[]>([]);
	const [primaryContactList, setPrimaryContactList] = useState<
		ClientContact[]
	>([]);
	const [isAddingForLinkedContacts, setIsAddingForLinkedContacts] =
		useState<boolean>(false);
	const [shouldUseDocumentTotal, setShouldUseDocumentTotal] =
		useState<boolean>(true);
	const [monthsToCalculateTotal, setMonthsToCalculateTotal] = useState<
		number | undefined
	>(defaultMonthsForMargin);
	const [addClientContactDrawerOpen, setAddClientContactDrawerOpen] =
		useState<boolean>(false);
	const [addClientDrawerOpen, setAddClientDrawerOpen] =
		useState<boolean>(false);
	const [
		isShareOpportunityWithVendorDrawerVisible,
		setIsShareOpportunityWithVendorDrawerVisible
	] = useState<boolean>(false);
	const [selectedLinkedClientContacts, setSelectedLinkedClientContacts] =
		useState<string[]>([]);

	useEffect(() => {
		return () => {
			if (location.pathname.includes(ZomentumRoutes.ClientsList)) {
				// Do Nothing
			} else {
				resetClientsDispatch();
				resetClientContactsDispatch();
			}
		};
	}, []);

	useEffect(() => {
		if (props.visible) {
			setCustomFieldList(customFieldListOpportunity);
			getClientsData();
			getOpportunityStages();
			setShouldUseDocumentTotal(true);
			setSelectedPipeline(currentActivePipeline);
			window.requestAnimationFrame(() => {
				setDefaultValuesForCustomFields();
			});
			if (props.activeClientCompanyId) {
				props.form.setFieldsValue({
					client_company_id: props.activeClientCompanyId
				});
				getClientContactsData(props.activeClientCompanyId);
			}
		}
	}, [props.visible]);

	useEffect(() => {
		if (props.activeOpportunityStage) {
			props.form.setFieldsValue({
				opportunity_pipeline_stage_id: props.activeOpportunityStage
			});
		}
	}, [props.activeOpportunityStage]);

	useEffect(() => {
		if (
			addOpportunityGuideDetail?.isOpportunityDrawerActive ||
			addOpportunityGuideDetail?.onboardingActiveStep ===
				Steps.HIGHLIGHT_OPPORTUNITY_SAVE_BUTTON
		) {
			window.requestAnimationFrame(() => {
				setDefaultValuesForCustomFields();
			});
		}
	}, [addOpportunityGuideDetail.isOpportunityDrawerActive]);

	const setDefaultValuesForCustomFields = () => {
		if (
			addOpportunityGuideDetail?.isOpportunityDrawerActive &&
			customFieldListOpportunity.length
		) {
			const custom_field_form_values: {
				[key: string]: string | moment.Moment;
			} = {};
			let opportunityType;
			customFieldListOpportunity?.forEach(field => {
				if (
					field.mandatory_field_details?.[
						CustomFieldMandatoryFieldDetailsValues.FOR_UI
					]
				) {
					if (
						field.system_field_id === "opportunity_opportunity_type"
					) {
						opportunityType = field.allowed_dropdown_values[0];
					} else {
						custom_field_form_values[field.id] =
							getCustomFieldsDefaultTypeValue(field);
					}
				}
			});
			props.form.setFieldsValue({
				custom_field_form_values
			});
			if (opportunityType) {
				props.form.setFieldsValue({
					opportunity_type: opportunityType
				});
			}
		}
	};

	const getClientsData = async () => {
		showOpportunitiesLoaderDispatch();
		try {
			const { clientList } = await getClientsDispatch(
				new ClientsRequest(
					new Array<TableQueryFilter>(),
					null,
					100,
					new Array<ChildEntityHydrationParams>(),
					null,
					props.activeClientCompanyId ?? null
				)
			);
			setClientList(clientList);
		} catch (error) {
			console.error(error);
		} finally {
			hideOpportunitiesLoaderDispatch();
		}
	};

	const getOpportunityStages = async () => {
		try {
			setIsPipelinesLoading(true);
			const response = await getAllPipelinesDispatch();
			let defaultPipeline: Pipeline[] | null = null;
			if (
				!currentActivePipeline ||
				isAllPipelinesFilter(currentActivePipeline.id)
			) {
				const defaults = filter(
					response.pipelineList,
					p => p.is_default === true
				);
				defaultPipeline =
					defaults.length > 0
						? defaults
						: response.pipelineList.slice(0, 1);
			} else {
				defaultPipeline = new Array(currentActivePipeline);
			}
			const opportunityStages =
				defaultPipeline?.[0]?.opportunity_stages || [];
			setOpportunityStages(opportunityStages);
			setIsPipelinesLoading(false);
			props.form.setFieldsValue({
				pipeline: defaultPipeline?.[0]?.id,
				opportunity_pipeline_id: defaultPipeline?.[0]?.id,
				opportunity_pipeline_stage_id:
					props.activeOpportunityStage || opportunityStages?.[0]?.id
			});
			const stageId =
				props.activeOpportunityStage ?? opportunityStages[0]?.id;
			if (stageId) {
				handleOpportunityStageChanged(stageId);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const getClientContactsData = async (
		selectedClient: string,
		clientContact?: ClientContact
	) => {
		if (!selectedClient) {
			return;
		}
		showOpportunitiesLoaderDispatch();
		try {
			const clientContactRequest = new ClientContactRequest(
				selectedClient
			);
			const response = await getClientContactsDispatch(
				clientContactRequest
			);
			setContactList(response?.clientContactList);
			setPrimaryContactList(
				response?.clientContactList.sort((a, _b) =>
					a.contact_type === ClientUserContactType.PrimaryContact
						? -1
						: 1
				)
			);

			const primaryContact = response?.clientContactList.filter(
				contact =>
					contact.contact_type ===
					ClientUserContactType.PrimaryContact
			)[0];

			const primaryContactFormValue = props.form.getFieldValue(
				"primary_client_user_id"
			);

			let defaultPrimaryContact: string;
			if (!!clientContact && !isAddingForLinkedContacts) {
				defaultPrimaryContact = clientContact.id;
			} else if (!!primaryContact) {
				defaultPrimaryContact = primaryContact.id;
			} else if (!!primaryContactFormValue) {
				defaultPrimaryContact = primaryContactFormValue;
			} else if (!!response?.clientContactList[0]) {
				defaultPrimaryContact = response?.clientContactList[0].id;
			} else {
				defaultPrimaryContact = "";
			}

			handleClientPrimaryUserSelect(defaultPrimaryContact);
			if (
				isAddingForLinkedContacts &&
				response?.clientContactList?.length > 1
			) {
				const listOfContacts = [
					...(props.form.getFieldValue("linked_client_user_ids") ??
						[]),
					clientContact?.id
				].filter(contact => contact?.id !== primaryContactFormValue);
				props.form.setFieldsValue({
					linked_client_user_ids: listOfContacts
				});
			}
		} catch (error) {
			console.error(error);
		} finally {
			setIsOpportunityLoading(false);
			setIsAddingForLinkedContacts(false);
			hideOpportunitiesLoaderDispatch();
		}
	};

	const onClose = (
		$event: React.MouseEvent<any> | React.KeyboardEvent<HTMLDivElement>
	) => {
		const handleDrawerClose = () => {
			props.onClose?.($event as any);
			resetFormEditedDispatch();
		};
		if (isFormEdited?.[FormKeys.AddOpportunity]) {
			handleDrawerClose();
		} else {
			ZModalConfirm({
				content:
					"Are you sure you want to discard creating this opportunity?",
				onOk: handleDrawerClose
			});
		}
	};

	const onSubmit = ($event: React.FormEvent<HTMLFormElement>) => {
		$event.preventDefault();
		props.form.validateFieldsAndScroll(
			{
				scroll: {
					alignWithTop: true
				}
			},
			(
				errors: unknown,
				addOpportunityFormValues: AddOpportunityFormValues
			) => {
				if (!errors && !isOpportunityLoading) {
					if (
						!!props.form.getFieldValue(
							"share_opportunity_with_vendors"
						)
					) {
						onOpenShareOpportunityWithVendorDrawer();
					} else {
						addOpportunity($event, addOpportunityFormValues);
					}
				}
			}
		);
	};

	const addOpportunity = async (
		$event: any,
		addOpportunityFormValues: AddOpportunityFormValues,
		shareOpportunityWithVendorRequest?: ShareOpportunityWithVendorRequest
	) => {
		setIsOpportunityLoading(true);
		try {
			let addOpportunityRequest = new AddOpportunityRequest({
				included_child_entities: [
					ChildEntityHydrationParams.CLIENT_COMPANIES_NAME,
					ChildEntityHydrationParams.VENDORS_ALL
				],
				...addOpportunityFormValues,
				owner_user_id:
					addOpportunityFormValues.owner_user_id ?? userData.id,
				mandatory_custom_field_check_require: true,
				manual_total: {
					...new OpportunityCostAndRevenueData(),
					...(addOpportunityFormValues.manual_total as OpportunityCostAndRevenueData),
					grand: 0
				}
			});
			const customFieldFormValues =
				addOpportunityFormValues.custom_field_form_values;
			if (customFieldFormValues && customFieldList?.length > 0) {
				addOpportunityRequest = {
					...addOpportunityRequest,
					custom_fields: processCustomFieldFormValues(
						customFieldFormValues,
						customFieldList
					)
				};
			}
			if (addOpportunityGuideDetail?.isOpportunityDrawerActive) {
				addOpportunityRequest = {
					...addOpportunityRequest,
					is_onboarding_entity: true
				};
			}
			if (
				shareOpportunityWithVendorRequest &&
				props.isMarketplacePipelineSyncingEnabled
			) {
				const selectedPipeline = pipelineList.find(
					pipeline =>
						pipeline.synced_vendor?.id ===
						shareOpportunityWithVendorRequest.vendor_company_id
				);
				if (
					selectedPipeline &&
					selectedPipeline?.id !==
						addOpportunityRequest?.opportunity_pipeline_id
				) {
					addOpportunityRequest.opportunity_pipeline_id =
						selectedPipeline.id;
					const stageId = selectedPipeline.opportunity_stages.find(
						stage => stage.is_initial_state
					)?.id;
					if (stageId)
						addOpportunityRequest.opportunity_pipeline_stage_id =
							stageId;
				}
			}
			const addOpportunityResponse = await addOpportunityDispatch(
				addOpportunityRequest,
				shareOpportunityWithVendorRequest
			);

			if (props.onSubmit) {
				props.onSubmit(addOpportunityResponse.addOpportunity);
			}

			ZMessage.success(
				`Opportunity successfully created${
					!!addOpportunityResponse.addOpportunity.vendor
						? ` and shared with ${addOpportunityResponse.addOpportunity.vendor.name}`
						: ""
				}`
			);
			props.onClose?.($event as any);
			if (
				props.shouldRedirectToOpportunity &&
				!!addOpportunityResponse?.addOpportunity?.id?.length
			) {
				setIsOpportunityLoading(false);

				history.push(
					ZomentumRoutes.OpportunityDetail.replace(
						":id",
						addOpportunityResponse.addOpportunity.id
					)
				);
			}
			await addOpportunityGuideDetail?.updateCreatedOpportunityDetail?.(
				addOpportunityResponse.addOpportunity.id
			);
			addOpportunityGuideDetail?.handleNext?.();
		} catch (error) {
			console.error(error);
			setIsOpportunityLoading(false);
		}
	};

	const addAndShareOpportunity = async (
		shareOpportunityWithVendorRequest: ShareOpportunityWithVendorRequest
	) => {
		await addOpportunity(
			null as any,
			props.form.getFieldsValue() as AddOpportunityFormValues,
			shareOpportunityWithVendorRequest
		);
		setIsShareOpportunityWithVendorDrawerVisible(false);
	};

	const showAddClientDrawer = () => {
		setAddClientDrawerOpen(true);
	};

	const closeAddClientDrawer = () => {
		getClientsData();
		setAddClientDrawerOpen(false);
	};

	const showAddClientContactDrawer = (
		defaultClientUserContactType: ClientUserContactType,
		isAddingForLinkedContacts: boolean
	) => {
		setDefaultClientUserContactType(defaultClientUserContactType);
		setIsAddingForLinkedContacts(isAddingForLinkedContacts);
		if (props.form.getFieldValue("client_company_id")) {
			setAddClientContactDrawerOpen(true);
		} else {
			ZMessage.error("Please select a Client first");
		}
	};

	const closeAddClientContactDrawer = () => {
		const selectedClient = props.form.getFieldValue("client_company_id");
		if (!selectedClient) {
			return;
		}
		const clientContactRequest = new ClientContactRequest(selectedClient);
		getClientContactsDispatch(clientContactRequest);
		setAddClientContactDrawerOpen(false);
	};

	const handleOpportunityStageChanged = (opportunityStageId: string) => {
		const opportunityStage = opportunityStages.find(
			stage => stage.id === opportunityStageId
		);
		if (opportunityStage) {
			setLossStageSelected(opportunityStage.is_terminal_lost_state);
			setWinStageSelected(opportunityStage.is_terminal_win_state);
		}
	};

	const handleAddNewClientCallback = async (client: Client) => {
		await getClientsData();
		handleClientSelect(client.id);
		getClientContactsData(client.id);
	};

	const handleAddNewClientContactCallback = async (
		clientContact: ClientContact
	) => {
		await getClientContactsData(
			clientContact.client_company_id,
			clientContact
		);
		await props.addClientContactOnSubmit?.(clientContact);
	};

	const handleClientSelect = async (clientId: string) => {
		props.form.setFieldsValue({
			linked_client_user_ids: undefined,
			primary_client_user_id: undefined,
			client_company_id: clientId
		});
		setSelectedClient(clientId);
		if (clientId) {
			await getClientContactsData(clientId);
		} else {
			setClientList(clientListData);
			resetClientContactsDispatch();
		}
	};

	const handleClientPrimaryUserSelect = (
		selectedPrimaryClientContact?: string
	) => {
		if (!selectedPrimaryClientContact) {
			setContactList(clientContactList);
			return;
		}
		props.form.setFieldsValue({
			primary_client_user_id: selectedPrimaryClientContact
		});
		setSelectedPrimaryClientContact(selectedPrimaryClientContact);
	};

	const handleClientLinkedUserSelect = (
		selectedLinkedClientContacts: string[]
	) => {
		props.form.setFieldsValue({
			linked_client_user_id: selectedLinkedClientContacts
		});
		setSelectedLinkedClientContacts(selectedLinkedClientContacts);
	};
	const [disableShareWithVendor, setDisableShareWithVendor] =
		useState<boolean>(false);
	useEffect(() => {
		if (props.visible) {
			let defaultPipeline: Pipeline[] | null = null;
			if (
				!currentActivePipeline ||
				isAllPipelinesFilter(currentActivePipeline.id)
			) {
				defaultPipeline = pipelineList.filter(p => p.is_default);
			} else {
				defaultPipeline = new Array(currentActivePipeline);
			}
			handlePipelineSelect(defaultPipeline?.[0]?.id);
		}
	}, [props.visible]);

	const handlePipelineSelect = (selectedPipelineId: string) => {
		const selectedPipeline = pipelineList.find(
			pipeline => pipeline.id === selectedPipelineId
		);
		const opportunityStages = selectedPipeline?.opportunity_stages || [];

		setSelectedPipeline(selectedPipeline || null);
		setOpportunityStages(opportunityStages);
		setLossStageSelected(
			opportunityStages[0] && opportunityStages[0].is_terminal_lost_state
		);
		setWinStageSelected(
			opportunityStages[0] && opportunityStages[0].is_terminal_win_state
		);
		setIsPipelinesLoading(false);
		props.form.setFieldsValue({
			opportunity_pipeline_id: selectedPipeline?.id,
			opportunity_pipeline_stage_id:
				opportunityStages[0] && opportunityStages[0].id,
			share_opportunity_with_vendors: !!selectedPipeline?.synced_vendor
		});
		setDisableShareWithVendor(!!selectedPipeline?.synced_vendor);
	};

	const handleMonthsChange = (value: number) => {
		setMonthsToCalculateTotal(value);
	};

	const fetchPrimaryContacts = async (value: string): Promise<void> => {
		const clientContactRequest = new ClientContactRequest(
			props.form.getFieldValue("client_company_id")
		);

		const selectedClientUser =
			props.form.getFieldValue("client_company_id");
		const primaryClientId = props.form.getFieldValue(
			"primary_client_user_id"
		);

		if (!selectedClientUser) {
			ZMessage.error("Please select a client");
			return;
		}

		clientContactRequest.clientId =
			props.form.getFieldValue("client_company_id");
		clientContactRequest.keywords = value;

		if (!value && primaryClientId) {
			clientContactRequest.selected_id = [primaryClientId];
		}

		if (
			primaryClientId &&
			props.form
				.getFieldValue("linked_client_user_ids")
				?.includes(primaryClientId)
		) {
			props.form.setFieldsValue({
				linked_client_user_ids: undefined
			});
		}

		try {
			const response = await getClientContacts(clientContactRequest);
			// directly calling service, no need to change redux state
			setPrimaryContactList(
				response.data.sort((a, _b) =>
					a.contact_type === ClientUserContactType.PrimaryContact
						? -1
						: 1
				)
			);
		} catch (error) {
			console.error(error);
		}
	};

	const fetchLinkedContacts = async (value: string): Promise<void> => {
		const selectedClientUser =
			props.form.getFieldValue("client_company_id");
		const clientContactRequest = new ClientContactRequest(
			selectedClientUser
		);
		const linkedContactIds = props.form.getFieldValue(
			"linked_client_user_ids"
		);

		if (!selectedClientUser) {
			ZMessage.error("Please select a client");
			return;
		}

		clientContactRequest.clientId =
			props.form.getFieldValue("client_company_id");
		clientContactRequest.keywords = value;

		if (!value) {
			clientContactRequest.selected_id = [linkedContactIds];
		}

		try {
			const response = await getClientContacts(clientContactRequest);
			// directly calling service, no need to change redux state

			setContactList(response.data);
		} catch (error) {
			console.error(error);
		}
	};

	const handleCheckboxChange = (e: CheckboxChangeEvent): void => {
		setShouldUseDocumentTotal(e.target.checked);
	};

	const getOpportunityFormItem = (customField: CustomField): ReactNode => {
		switch (customField.system_field_id) {
			case "opportunity_opportunity_type": {
				return (
					<Col span={24}>
						<Form.Item
							label={customField.display_name}
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator("opportunity_type", {
								rules: [
									{
										required:
											customField
												.mandatory_field_details?.[
												CustomFieldMandatoryFieldDetailsValues
													.FOR_UI
											],
										message:
											"Please choose the opportunity type"
									}
								]
							})(
								<Select<OpportunityType>
									getPopupContainer={trigger =>
										trigger.parentNode
									}
									placeholder="Please choose the opportunity type"
									allowClear
								>
									{customField.allowed_dropdown_values.map(
										value => (
											<Select.Option
												key={value}
												value={value}
											>
												{value}
											</Select.Option>
										)
									)}
								</Select>
							)}
						</Form.Item>
					</Col>
				);
			}
			case "opportunity_opportunity_pipeline_stage_id": {
				const options = opportunityStages.map(stage => ({
					name: (
						<Fragment>
							{stage.stage_name}
							{stage.is_terminal_win_state && <ZWonTag />}
							{stage.is_terminal_lost_state && <ZLostTag />}
						</Fragment>
					),
					value: stage.id
				}));
				return (
					<Fragment>
						<Col span={24}>
							<Form.Item
								label="Stage"
								labelAlign="left"
								hasFeedback={true}
							>
								{props.form.getFieldDecorator(
									"opportunity_pipeline_stage_id",
									{
										rules: [
											{
												required: true,
												message:
													"Please choose the Opportunity stage"
											}
										],
										initialValue:
											props.activeOpportunityStage ||
											(opportunityStages[0] &&
												opportunityStages[0]?.id)
									}
								)(
									<ZSelect<string>
										allowClear={true}
										getPopupContainer={trigger =>
											trigger.parentNode
										}
										loading={isLoading}
										placeholder="Please choose a opportunity stage"
										className="add-opportunity-clients-dropdown-container"
										onChange={handleOpportunityStageChanged}
										selectOptions={options}
										data-testid="pipeline-stage-select"
									/>
								)}
							</Form.Item>
						</Col>
						{props.form.getFieldValue(
							"opportunity_pipeline_stage_id"
						) &&
							winStageSelected && (
								<Col span={24}>
									<Form.Item
										label="Won reason"
										labelAlign="left"
										hasFeedback={true}
									>
										{props.form.getFieldDecorator(
											"won_reason",
											{
												rules: [
													{
														required: false,
														message:
															"Please enter won reason",
														whitespace: true
													}
												]
											}
										)(
											<Input placeholder="Please enter won reason" />
										)}
									</Form.Item>
								</Col>
							)}
						{props.form.getFieldValue(
							"opportunity_pipeline_stage_id"
						) &&
							lossStageSelected && (
								<Col span={24}>
									<Form.Item
										label="Loss reason"
										labelAlign="left"
										hasFeedback={true}
									>
										{props.form.getFieldDecorator(
											"loss_reason",
											{
												rules: [
													{
														required: false,
														message:
															"Please enter loss reason",
														whitespace: true
													}
												]
											}
										)(
											<Input placeholder="Please enter loss reason" />
										)}
									</Form.Item>
								</Col>
							)}
					</Fragment>
				);
			}
			case "opportunity_owner_user_id": {
				return (
					<Col span={24}>
						<Form.Item
							label="Owner"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator("owner_user_id", {
								rules: [
									{
										required:
											customFieldListOpportunity.find(
												field =>
													field.system_field_id ==
													"opportunity_owner_user_id"
											)?.mandatory_field_details?.for_ui,
										message: "Please choose owner"
									}
								],
								initialValue:
									userData &&
									userData.id &&
									userData.id.length > 0
										? userData.id
										: null
							})(
								<ZUserSelect
									data-testid="add-opportunity-owner"
									showSearch={true}
									allowClear={true}
									loading={isLoading || isUserLoading}
									disabled={!hasOpportunityAssignPermission}
									placeholder="Please choose owner"
									className="add-opportunity-owner-container"
									filterOption={filterOptionsOnSearch}
								/>
							)}
						</Form.Item>
					</Col>
				);
			}
			case "opportunity_primary_client_user_id": {
				const linkedContactsList = props.form.getFieldValue(
					"linked_client_user_ids"
				);
				const options = primaryContactList
					.filter(
						contact => !linkedContactsList?.includes(contact?.id)
					)
					.map(contact => ({
						name: `${contact.name.full} ${
							contact.email ? `<${contact.email}>` : ""
						}`,
						value: contact.id
					}));

				return (
					<Col span={24}>
						<Form.Item
							label="Opportunity Primary Contact"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator(
								"primary_client_user_id",
								{
									rules: [
										{
											required:
												customFieldListOpportunity.find(
													field =>
														field.system_field_id ==
														"opportunity_primary_client_user_id"
												)?.mandatory_field_details
													?.for_ui,
											message:
												"Please choose primary contact"
										}
									],
									initialValue: selectedPrimaryClientContact
								}
							)(
								<ZDynamicSelect
									fetchData={fetchPrimaryContacts}
									placeholder="Please choose opportunity primary contact"
									allowClear
									options={options}
									onSelect={() => fetchPrimaryContacts("")}
									shouldFetchDefaultData
									dropdownRender={menu => (
										<div>
											{menu}
											<ZFeatureAccess
												requiredPlan={
													PricingPlanNames.IGNITE
												}
												requiredRole={UserRole.UserRole}
												permissions={
													clientCompanyPermssions
												}
											>
												{shouldShowAddEntityActions() && (
													<div
														className="add-entity-action"
														onMouseDown={$event => {
															$event.preventDefault();
														}}
														onClick={() =>
															showAddClientContactDrawer(
																!!primaryContactList.length
																	? ClientUserContactType.SecondaryContact
																	: ClientUserContactType.PrimaryContact,
																false
															)
														}
													>
														<ZTypography.ZText className="text-blue-6">
															Add Contact
														</ZTypography.ZText>
													</div>
												)}
											</ZFeatureAccess>
										</div>
									)}
								/>
							)}
						</Form.Item>
					</Col>
				);
			}
			case "opportunity_linked_client_user_ids": {
				const options = contactList
					.filter(
						contact =>
							contact?.id !==
							props.form.getFieldValue("primary_client_user_id")
					)
					.map(contact => ({
						name: `${contact.name.full} ${
							contact.email ? `<${contact.email}>` : ""
						}`,
						value: contact?.id
					}));

				return (
					<Col span={24}>
						<Form.Item
							label="Linked Contacts"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator(
								"linked_client_user_ids",
								{
									rules: [
										{
											required:
												customFieldListOpportunity.find(
													field =>
														field.system_field_id ===
														"opportunity_linked_client_user_ids"
												)?.mandatory_field_details
													?.for_ui,
											message:
												"Please choose a client contact"
										}
									]
								}
							)(
								<ZDynamicSelect
									fetchData={fetchLinkedContacts}
									placeholder="Please choose linked contacts"
									allowClear
									options={options}
									onBlur={() => fetchLinkedContacts("")}
									shouldFetchDefaultData
									isMultiSelect
									dropdownRender={menu => (
										<div>
											{menu}
											<ZFeatureAccess
												requiredPlan={
													PricingPlanNames.IGNITE
												}
												requiredRole={UserRole.UserRole}
												permissions={
													clientCompanyPermssions
												}
											>
												{shouldShowAddEntityActions() && (
													<div
														className="add-entity-action"
														onMouseDown={$event => {
															$event.preventDefault();
														}}
														onClick={() =>
															showAddClientContactDrawer(
																!!primaryContactList.length
																	? ClientUserContactType.SecondaryContact
																	: ClientUserContactType.PrimaryContact,
																true
															)
														}
													>
														<ZTypography.ZText className="text-blue-6">
															Add Contact
														</ZTypography.ZText>
													</div>
												)}
											</ZFeatureAccess>
										</div>
									)}
								/>
							)}
						</Form.Item>
					</Col>
				);
			}
			case "opportunity_estimated_close_date": {
				return (
					<Col span={24}>
						<Form.Item
							label="Expected Close Date"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator(
								"estimated_close_date",
								{
									rules: [
										{
											required:
												customFieldListOpportunity.find(
													field =>
														field.system_field_id ==
														"opportunity_estimated_close_date"
												)?.mandatory_field_details
													?.for_ui,
											message: "Please choose a date"
										}
									],
									initialValue: initialEstimatedCloseDate
								}
							)(
								<ZDatePicker
									allowClear={false}
									className="date-picker"
									onChange={(
										estimatedCloseDate: Moment | null
									) => {
										setEstimatedCloseDate(
											estimatedCloseDate
										);
									}}
								/>
							)}
						</Form.Item>
					</Col>
				);
			}
			case "opportunity_client_po_number": {
				return (
					<Col span={24}>
						<Form.Item
							label="Client PO Number"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator("client_po_number", {
								rules: [
									{
										required: false,
										message:
											"Please enter client PO number",
										whitespace: true
									}
								]
							})(
								<Input placeholder="Please enter client PO number" />
							)}
						</Form.Item>
					</Col>
				);
			}
			case "opportunity_close_probability": {
				return (
					<Col span={24}>
						<Form.Item
							label="Close Probability"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator("close_probability", {
								rules: [
									{
										required: false,
										message:
											"Please enter a close probability"
									}
								],
								initialValue: 20
							})(
								<InputNumber<number>
									min={0}
									step={10}
									max={100}
									formatter={$event => `${$event}%`}
									parser={$event =>
										$event
											? parseInt(
													$event
														.replace("%", "")
														.trim()
											  )
											: 0
									}
									placeholder="Please enter a close probability"
									className="w-full"
								/>
							)}
						</Form.Item>
					</Col>
				);
			}
			case "opportunity_estimated_value": {
				return (
					<Col span={24}>
						<Form.Item
							label="Estimated Value"
							labelAlign="left"
							hasFeedback={true}
						>
							<Input.Group>
								{props.form.getFieldDecorator(
									"estimated_value",
									{
										rules: [
											{
												required:
													customFieldListOpportunity.find(
														field =>
															field.system_field_id ===
															"opportunity_estimated_value"
													)?.mandatory_field_details
														?.for_ui,
												message:
													"Please enter estimated value"
											}
										]
									}
								)(
									<InputNumber
										type="number"
										step=".01"
										min={0}
										className="has-currency-addon"
										placeholder="Please enter estimated value"
										onChange={(estimatedValue?: number) =>
											setEstimatedValue(estimatedValue)
										}
									/>
								)}
								<Input
									placeholder={
										props.form.getFieldValue("currency") ||
										getCurrencySettings().currency
									}
									disabled
									className="currency-addon"
								/>
							</Input.Group>
						</Form.Item>
					</Col>
				);
			}

			case "opportunity_use_document_totals": {
				return (
					<Col span={24}>
						<Form.Item>
							{props.form.getFieldDecorator(
								"use_document_totals",
								{
									initialValue: true
								}
							)(
								<ZCheckbox
									defaultChecked={true}
									onChange={handleCheckboxChange}
									checkboxOption="Use Revenue/Cost of Documents"
									data-testid="use-document-totals"
								/>
							)}
						</Form.Item>
					</Col>
				);
			}

			case "opportunity_months_to_calculate_margin": {
				return (
					<Col span={24}>
						<Form.Item
							label="Calculate total for # months"
							labelCol={{ span: 8 }}
							wrapperCol={{ span: 14 }}
							labelAlign="left"
							colon={false}
						>
							{props.form.getFieldDecorator(
								"months_to_calculate_margin",
								{
									initialValue: monthsToCalculateTotal
								}
							)(
								<ZInputNumber
									min={1}
									width={InputNumberWidths.lg}
									onChange={handleMonthsChange}
									precision={0}
								/>
							)}
						</Form.Item>
					</Col>
				);
			}

			case "opportunity_manual_total": {
				return (
					<Col span={24}>
						<ZOppRevenueAndCostFields
							form={props.form}
							shouldUseDocumentTotal={shouldUseDocumentTotal}
							isFormInEditOpportunity={false}
							shouldShowExtraFields={false}
							monthsToCalculateTotal={monthsToCalculateTotal}
						/>
					</Col>
				);
			}

			case "opportunity_source": {
				return (
					<Col span={24}>
						<Form.Item
							label="Source"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator("source", {
								rules: [
									{
										required: false,
										message: "Please enter source",
										whitespace: true
									}
								]
							})(<Input placeholder="Please enter source" />)}
						</Form.Item>
					</Col>
				);
			}
			case "opportunity_campaign": {
				return (
					<Col span={24}>
						<Form.Item
							label="Campaign"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator("campaign", {
								rules: [
									{
										required: false,
										message: "Please enter campaign",
										whitespace: true
									}
								]
							})(<Input placeholder="Please enter campaign" />)}
						</Form.Item>
					</Col>
				);
			}
			case "opportunity_vendor_details":
				return null;
			default: {
				return (
					<Col span={24}>
						<RenderCustomFieldFormItem
							form={props.form}
							customField={customField}
							formAlignment={formAlignment}
						/>
					</Col>
				);
			}
		}
	};

	const onOpenShareOpportunityWithVendorDrawer = () => {
		setIsShareOpportunityWithVendorDrawerVisible(true);
	};

	const onCloseShareOpportunityWithVendorDrawer = () => {
		setIsShareOpportunityWithVendorDrawerVisible(false);
	};

	const getAllSelectedPrimaryAndLinkedContacts = () => {
		const primaryClientContactId = props.form.getFieldValue(
			"primary_client_user_id"
		);
		const linkedClientContactsIds = props.form.getFieldValue(
			"linked_client_user_ids"
		);
		let allSelectedIds: string[] = [];
		if (primaryClientContactId) {
			allSelectedIds.push(primaryClientContactId);
		}
		if (!!linkedClientContactsIds?.length) {
			allSelectedIds = allSelectedIds.concat(linkedClientContactsIds);
		}
		return clientContactList.filter(clientContact =>
			allSelectedIds.includes(clientContact.id)
		);
	};

	const updateEstimatedValue = (estimatedValue: number): void => {
		setEstimatedValue(estimatedValue);
		props.form.setFieldsValue({
			estimated_value: estimatedValue
		});
	};

	const updateEstimatedCloseDate = (
		estimatedCloseDate?: Moment | null
	): void => {
		setEstimatedCloseDate(estimatedCloseDate);
		props.form.setFieldsValue({
			estimated_close_date: estimatedCloseDate
		});
	};

	const newClientList = clientList.slice();
	const demoClientList = addOpportunityGuideDetail?.demoClientsData;

	const hasClient = clientList.some(
		client => client.id === props.activeClientCompanyId
	);

	if (!hasClient && !isClientAssigned) {
		newClientList.push({
			name: props.activeClientName,
			id: props.activeClientCompanyId
		} as Client);
	}

	const pipelineListOptions: IOptionProps[] = pipelineList?.map(pipeline => {
		return {
			name: capitalizeFirstLetter(pipeline.name),
			value: pipeline.id
		};
	});

	props.form.getFieldDecorator("opportunity_pipeline_id");

	const SaveButton = (
		<ZButton
			type="primary"
			htmlType="submit"
			disabled={isLoading}
			loading={isLoading}
			data-testid="submit-button"
			id={
				addOpportunityGuideDetail?.stepDetail?.guideUIProps
					?.highlightId ?? ""
			}
		>
			{!!props.form.getFieldValue("share_opportunity_with_vendors")
				? "Save and Share"
				: "Save"}
		</ZButton>
	);

	const SaveOpportunityButtonGuideWrapper =
		addOpportunityGuideDetail?.SaveOpportunityButtonGuide;

	const OpportunityDrawerGuideWrapper =
		addOpportunityGuideDetail?.OpportunityDrawerGuide;

	if (
		demoClientList &&
		addOpportunityGuideDetail?.isOpportunityDrawerActive
	) {
		newClientList.unshift(demoClientList);
	}
	const TitleColumn = (
		<Col
			span={24}
			id={
				addOpportunityGuideDetail?.OpportunityDrawerStepDetail
					?.guideUIProps?.highlightId ?? ""
			}
		>
			<Form.Item label="Title" labelAlign="left" hasFeedback={true}>
				{props.form.getFieldDecorator("name", {
					rules: [
						{
							required: true,
							message: "Please enter opportunity name",
							whitespace: true
						}
					],
					initialValue:
						addOpportunityGuideDetail?.isOpportunityDrawerActive
							? `Demo Opportunity ${nextOpportunityId}`
							: ""
				})(
					<Input
						placeholder="Please enter opportunity name"
						data-testid="add-opportunity_title"
					/>
				)}
			</Form.Item>
		</Col>
	);

	const ShareWithVendorCheckboxGuideWrapper =
		addOpportunityGuideDetail?.ShareWithVendorCheckboxGuide;

	const DrawerFooter = (
		<div
			className="ant-drawer-footer items-center"
			id={
				addOpportunityGuideDetail?.ShareWithVendorCheckboxStepDetail
					?.guideUIProps?.highlightId ?? ""
			}
		>
			{showPartnerAlignFeaturesFlag &&
				!lossStageSelected &&
				!winStageSelected &&
				!isPipelinesLoading &&
				!isLoading && (
					<Form.Item
						className="m-0"
						wrapperCol={{ span: 24 }}
						labelAlign="left"
					>
						{props.form.getFieldDecorator(
							"share_opportunity_with_vendors",
							{
								valuePropName: "checked",
								initialValue:
									currentActivePipeline &&
									!isAllPipelinesFilter(
										currentActivePipeline.id
									)
										? !!currentActivePipeline?.synced_vendor
										: !!pipelineList.filter(
												pipeline => pipeline.is_default
									  )?.[0].synced_vendor
							}
						)(
							<ZCheckbox disabled={disableShareWithVendor}>
								Share Opportunity With Vendor
							</ZCheckbox>
						)}
					</Form.Item>
				)}
			<div className="flex-1" />
			<ZButton
				type="default"
				htmlType="button"
				onClick={onClose}
				disabled={isLoading}
				className="mr-4"
			>
				Cancel
			</ZButton>
			{!!SaveOpportunityButtonGuideWrapper ? (
				<SaveOpportunityButtonGuideWrapper>
					{SaveButton}
				</SaveOpportunityButtonGuideWrapper>
			) : (
				SaveButton
			)}
		</div>
	);

	const handleFormUnsaveChange = () => {
		!isFormEdited?.[FormKeys.AddOpportunity] && setFormEditedDispatch();
	};

	return (
		<ZDrawer
			visible={props.visible}
			closable={true}
			destroyOnClose={true}
			onClose={onClose}
			className={`add-opportunity-drawer zomentum-drawer ${
				isShareOpportunityWithVendorDrawerVisible
					? "shared-opportunity-with-vendor-drawer-opened"
					: ""
			}
                    ${
						addOpportunityGuideDetail?.isOpportunityDrawerActive
							? "add-opportunity-guide-drawer"
							: ""
					}`}
			title={
				<Row>
					<Col span={24}>
						<div className="drawer-title-container">
							<div className="drawer-title">Add Opportunity</div>
						</div>
					</Col>
				</Row>
			}
		>
			<AddClientDrawer
				visible={addClientDrawerOpen}
				onClose={closeAddClientDrawer}
				onSubmit={handleAddNewClientCallback}
			/>
			<AddClientContactDrawer
				clientId={props.form.getFieldValue("client_company_id")}
				visible={addClientContactDrawerOpen}
				onClose={closeAddClientContactDrawer}
				activeTabContactType={defaultClientUserContactType}
				onSubmit={handleAddNewClientContactCallback}
			/>
			<Form
				layout="horizontal"
				labelCol={formAlignment.labelCol}
				wrapperCol={formAlignment.wrapperCol}
				onSubmit={onSubmit}
				className="add-opportunity-drawer-form"
				onSelectCapture={handleFormUnsaveChange}
				onChangeCapture={handleFormUnsaveChange}
			>
				<Row className="drawer-form-container pb-4">
					{!!OpportunityDrawerGuideWrapper ? (
						<OpportunityDrawerGuideWrapper>
							{TitleColumn}
						</OpportunityDrawerGuideWrapper>
					) : (
						TitleColumn
					)}
					<Col span={24}>
						<Form.Item
							label="Client"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator("client_company_id", {
								rules: [
									{
										required: true,
										message: "Please choose a Client"
									}
								],
								initialValue:
									!!newClientList?.length &&
									addOpportunityGuideDetail?.isOpportunityDrawerActive
										? demoClientList?.id
										: null
							})(
								<ZClientSelect
									data-testid="add-opportunity-client"
									onChange={handleClientSelect}
									loading={
										isLoading ||
										isClientsLoading ||
										isClientLoading
									}
									placeholder="Please choose a client"
									className="add-opportunity-clients-dropdown-container"
									defaultOptions={newClientList}
									dropdownRender={menu => (
										<div>
											{menu}
											<ZFeatureAccess
												requiredPlan={
													PricingPlanNames.IGNITE
												}
												requiredRole={UserRole.UserRole}
												permissions={
													clientCompanyPermssions
												}
											>
												{shouldShowAddEntityActions() && (
													<div
														className="add-entity-action"
														onMouseDown={$event => {
															$event.preventDefault();
														}}
														onClick={
															showAddClientDrawer
														}
													>
														Add Client
													</div>
												)}
											</ZFeatureAccess>
										</div>
									)}
								/>
							)}
						</Form.Item>
					</Col>
					{showCurrencyDropdown && (
						<Col span={24}>
							<Form.Item
								label="Currency"
								labelAlign="left"
								hasFeedback={true}
							>
								{props.form.getFieldDecorator("currency", {
									initialValue: null
								})(<OpportunityCurrencyDropdown />)}
							</Form.Item>
						</Col>
					)}
					<Col span={24}>
						<Form.Item
							label="Pipeline"
							labelAlign="left"
							hasFeedback={true}
						>
							{props.form.getFieldDecorator("pipeline", {
								rules: [
									{
										required: true,
										message: "Please choose a Pipeline"
									}
								]
							})(
								<ZSelect<string>
									getPopupContainer={trigger =>
										trigger.parentNode
									}
									onChange={handlePipelineSelect}
									loading={isPipelinesLoading}
									placeholder="Please choose a pipeline"
									className="add-opportunity-clients-dropdown-container"
									data-testid="pipeline-select"
									selectOptions={pipelineListOptions}
								/>
							)}
						</Form.Item>
					</Col>

					{customFieldList?.length > 0 &&
						customFieldList.map(customField => {
							return (
								<Fragment key={customField.id}>
									{getOpportunityFormItem(customField)}
								</Fragment>
							);
						})}
				</Row>

				{!!ShareWithVendorCheckboxGuideWrapper ? (
					<ShareWithVendorCheckboxGuideWrapper>
						{DrawerFooter}
					</ShareWithVendorCheckboxGuideWrapper>
				) : (
					DrawerFooter
				)}
			</Form>
			<ShareOpportunityWithVendorDrawer
				visible={isShareOpportunityWithVendorDrawerVisible}
				onClose={onCloseShareOpportunityWithVendorDrawer}
				clientContactList={getAllSelectedPrimaryAndLinkedContacts()}
				onShareOpportunity={addAndShareOpportunity}
				selectedPipelineVendorId={
					selectedPipeline?.synced_vendor?.id ?? null
				}
				estimatedValue={estimatedValue}
				updateEstimatedValue={updateEstimatedValue}
				estimatedCloseDate={estimatedCloseDate}
				updateEstimatedCloseDate={updateEstimatedCloseDate}
				selectedClient={selectedClient ?? props.activeClientCompanyId}
				selectedPrimaryContact={selectedPrimaryClientContact}
				updateSelectedPrimaryContact={handleClientPrimaryUserSelect}
				selectedLinkedContacts={selectedLinkedClientContacts}
				updateSelectedLinkedContacts={handleClientLinkedUserSelect}
			/>
		</ZDrawer>
	);
};

export default Form.create<Props>({ name: "add_opportunity_form" })(
	(props: Props) => {
		return <AddOpportunityDrawerForm {...props} />;
	}
);
